import type {
  Bootstrap,
  Cadence,
  Notebook,
  ReportConfig,
  Schedule,
} from "./reportTypes"

/** Reopening a report restores the date range shown in its saved artifact. */
export function recipeFromNotebook(notebook: Notebook): ReportConfig {
  return {
    // A persisted All agents selection may be 0; recipes dispatch the null form.
    scope: {
      ...structuredClone(notebook.scope),
      top_n: notebook.scope.top_n || null,
      ...(notebook.scope.group_by_metric_id
        ? { group_by_metric_id: notebook.scope.group_by_metric_id }
        : {}),
    },
    cadence: notebook.cadence ?? "weekly",
    instructions: notebook.instructions ?? "",
    ...(notebook.period_start
      ? { period_start: notebook.period_start.slice(0, 10) }
      : {}),
    period_end: notebook.period.end.slice(0, 10),
  }
}

export function latestNotebookRevision(notebook: Notebook): number {
  return Math.max(
    notebook.revision,
    ...(notebook.revisions ?? []).map((entry) => entry.revision),
  )
}

/** Keeps the mounted catalog truthful after a delete, so a failed refresh cannot resurface the recipe. */
export function withoutSchedule(
  bootstrap: Bootstrap | null,
  id: string,
): Bootstrap | null {
  return (
    bootstrap && {
      ...bootstrap,
      schedules: bootstrap.schedules.filter((item) => item.id !== id),
    }
  )
}

/**
 * Keeps the mounted catalog truthful after a schedule is saved or changed, so
 * a failed refresh can neither hide a new schedule nor revert a control.
 */
export function withSchedule(
  bootstrap: Bootstrap | null,
  schedule: Schedule,
): Bootstrap | null {
  return (
    bootstrap && {
      ...bootstrap,
      schedules: bootstrap.schedules.some((item) => item.id === schedule.id)
        ? bootstrap.schedules.map((item) =>
            item.id === schedule.id ? schedule : item,
          )
        : [...bootstrap.schedules, schedule],
    }
  )
}

/**
 * A refreshed schedule list may omit which notebook each schedule repeats;
 * keep the template the mounted list already knows so a successful refresh
 * cannot erase the Repeats badge the schedule just earned.
 */
export function withKnownScheduleTemplates(
  fresh: Bootstrap,
  known: Bootstrap | null,
): Bootstrap {
  if (!known) {
    return fresh
  }
  return {
    ...fresh,
    // Only an omitted field is filled in; an explicit null means the schedule
    // no longer repeats a notebook and must stay null.
    schedules: fresh.schedules.map((schedule) =>
      schedule.template_notebook_id !== undefined
        ? schedule
        : {
            ...schedule,
            template_notebook_id:
              known.schedules.find((item) => item.id === schedule.id)
                ?.template_notebook_id ?? null,
          },
    ),
  }
}

/**
 * Cadences of the schedules that repeat this notebook. A schedule's template
 * stays pinned to one revision, but renames and edits add revisions to the
 * same notebook, so this follows the notebook. Editions a schedule generates
 * are notebooks of their own and are not counted.
 */
export function notebookRepeats(
  bootstrap: Bootstrap | null,
  notebook: Notebook | null,
): Cadence[] {
  return notebook
    ? (bootstrap?.schedules ?? [])
        .filter((item) => item.template_notebook_id === notebook.id)
        .map((item) => item.cadence)
    : []
}

export function reportRevisionHistory(notebook: Notebook) {
  return (
    notebook.revisions ??
    Array.from({ length: notebook.revision }, (_, index) => ({
      revision: index + 1,
      created_at: "",
    }))
  )
}
